/*
 * DISTRIBUTION STATEMENT C. Distribution authorized to U.S. Government Agencies and their contractors; Critical
 * Technology; 19 October 2017. Other requests for this document shall be referred to Chemical and Biological
 * Technologies, Defense Threat Reduction Agency, 8725 John J Kingman Road, Fort Belvoir, VA 22060-6201.
 *
 * WARNING: This document contains technical data whose export is restricted by the Arms Export Control Act (Sec 2751
 * of Title 22, United States Code) or the Export Control Reform Act of 2018 (Chapter 58 Sections 4801-4852 of Title
 * 50, United States Code). Violations of these export laws are subject to severe criminal penalties. Disseminate in
 * accordance with provisions of DoD Directive 5230.25 and DoD Instruction 2040.02. DESTRUCTION NOTICE: For
 * unclassified, limited distribution documents, destroy by any method that will prevent disclosure of the contents
 * or reconstruction of the document.
 *
 * This material is based upon work supported by the Under Secretary of War for Research and Engineering under Air
 * Force Contract No. FA8702-15-D-0001 or FA8702-25-D-B002. Any opinions, findings, conclusions or recommendations
 * expressed in this material are those of the author(s) and do not necessarily reflect the views of the Under
 * Secretary of War for Research and Engineering.
 *
 * © 2026 Massachusetts Institute of Technology.
 *
 * The software/firmware is provided to you on an As-Is basis
 *
 * Delivered to the U.S. Government with Unlimited Rights, as defined in DFARS Part 252.227-7013 or 7014 (Feb 2014).
 * Notwithstanding any copyright notice, U.S. Government rights in this work are defined by DFARS 252.227-7013 or
 * DFARS 252.227-7014 as detailed above. Use of this work other than as specifically authorized by the U.S.
 * Government may violate any copyrights that exist in this work.
 *
 * This document contains Controlled Unclassified Information (CUI). Handling of this information is subject to the
 * controls identified in DFARS 252.204-7012 - SAFEGUARDING COVERED DEFENSE INFORMATION AND CYBER INCIDENT REPORTING.
 */

import {OpenDocumentsPage} from "../pages/DocumentsPage";

let abortControllers = {};

function overrideEnvVarsIfRequested() {
  // Allow process.env.*, if present, to override window._env_.*.
  // This is useful for local development (like "npm start").
  //
  // Note that process.env.* is set at React build/start time if
  // a .env.* file is present, but no .env file is included in
  // built Docker images. In contrast, window._env_.* is set in
  // public/env-config.js, which is overwritten at container launch
  // time to allow the same UI image to run on a variety of hosts.
  //
  // So if process.env.* is set, we're in a local dev environment,
  // and we should respect process.env and override window._env_.*.
  // If it isn't set, then we're running in a container, and
  // we want to leave window._env_.* alone.

  console.log('process.env ', process.env);
  if (process.env.REACT_APP_API) {
    window._env_ = {
      APP_SITE: process.env.REACT_APP_SITE,
      APP_NAME: process.env.REACT_APP_APPLICATION_NAME,
      APP_CHALLENGE_NAME: process.env.REACT_APP_CHALLENGE_NAME,
      APP_URI: process.env.REACT_APP_URI,
      APP_HOSTNAME: process.env.REACT_APP_HOSTNAME,
      APP_NAME_POSSESSIVE: process.env.REACT_APP_APPLICATION_NAME_POSSESSIVE,
      APP_TAG_LINE: process.env.REACT_APP_TAG_LINE,
      APP_SUPPORT_EMAIL: process.env.REACT_APP_SUPPORT_EMAIL,
      APP_API: process.env.REACT_APP_API,
      APP_SECURE_API: null,
      CH_API: process.env.REACT_APP_CH_API,
      CH_SECURE_API: null,
      APP_LOGOUT: process.env.REACT_APP_LOGOUT,
      APP_REGISTER: process.env.REACT_APP_REGISTRATION,
      APP_PRODUCTION: process.env.REACT_APP_PRODUCTION,
      CUI_APP: process.env.REACT_APP_CUI_APP === 'true',
      FEATURE_COHORT: process.env.REACT_APP_FEATURE_COHORT === 'true',
      FEATURE_PREVIEW: true, //process.env.REACT_APP_FEATURE_PREVIEW === 'true',
      FEATURE_AW: true, //process.env.REACT_APP_FEATURE_AW === 'true',
      FEATURE_CLI_DOWNLOAD: process.env.REACT_APP_FEATURE_CLI_DOWNLOAD === 'true',
      FEATURE_CLI_UPLOAD: process.env.REACT_APP_FEATURE_CLI_UPLOAD === 'true',
      FEATURE_ORG_AC: process.env.REACT_APP_FEATURE_ORG_AC === 'true',
      FEATURE_PIPELINE: true, // process.env.REACT_APP_FEATURE_PIPELINE === 'true',
      FEATURE_STUDYCARD: true, // process.env.REACT_APP_FEATURE_STUDYCARD === 'true',
      TOOLS_PAGE: true, //process.env.REACT_APP_TOOLS_PAGE === 'true',
      SHOW_DISCLAIMER: true,
      LOCALHOST: process.env.REACT_APP_LOCALHOST === 'true'
    }
  }
  console.log('window._env_ ', window._env_);
}

// Call overrideEnvVarsIfRequested once from global scope so all
// future API clients will have window._env_.* properly initialized.
overrideEnvVarsIfRequested();

function removeLeadingSlash(endpoint) {
  if (endpoint.charAt(0) === '/') {
    return endpoint.substring(1);
  }
  return endpoint;
}

// Use fetch to make a get call to the API.
export const performGetFetch = (endpoint, success, failure,
                                override, printMessage, fullEndpoint) => {
  if (!endpoint) {
    return;
  }
  endpoint = removeLeadingSlash(endpoint);

  // By default we don't want to restart a fetch if one is already running for this endpoint.
  if (!override && abortControllers[endpoint]) {
    if (printMessage) {
      console.warn('Request already in progress: ', endpoint);
    }
    return false;
  }

  let requestOptions = {};

  requestOptions.signal = createAbortController(endpoint, printMessage).signal;
  fullEndpoint = fullEndpoint || buildApiUrl(endpoint);

  return fetch(fullEndpoint, requestOptions)
    .then((res) => {
      delete abortControllers[endpoint];
      if (!res.ok) {
        return res.json().then(errorData => {
          return Promise.reject(errorData);
        });
      }

      if (res.headers.get('Content-Type')?.includes('application/json')) {
        return res.json();
      } else {
        return res.text();
      }
    })
    .then(
      (result) => {
        return success(result);
      },
      (error) => {
        return failure(error);
      }
    )
    .catch((error) => {
      return failure(error);
    });
}



// this is what performGetFetch should be...
// adding in requestOptions as parameter after endpoint.
// this is in alignment with POST call now.
export const performGetFetch2 = (endpoint, requestOptions = {}, success, failure,
                                override, printMessage, fullEndpoint) => {
  if (!endpoint) {
    return;
  }
  endpoint = removeLeadingSlash(endpoint);

  // By default we don't want to restart a fetch if one is already running for this endpoint.
  if (!override && abortControllers[endpoint]) {
    if (printMessage) {
      console.warn('Request already in progress: ', endpoint);
    }
    return false;
  }

  requestOptions.signal = createAbortController(endpoint, printMessage).signal;
  fullEndpoint = fullEndpoint || buildApiUrl(endpoint);

  return fetch(fullEndpoint, requestOptions)
    .then((res) => {
      delete abortControllers[endpoint];
      if (!res.ok) {
        return res.json().then(errorData => {
          return Promise.reject(errorData);
        });
      }

      if (res.headers.get('Content-Type')?.includes('application/json')) {
        return res.json();
      } else {
        return res.text();
      }
    })
    .then(
      (result) => {
        return success(result);
      },
      (error) => {
        return failure(error);
      }
    )
    .catch((error) => {
      return failure(error);
    });
}


// Use XMLHttpRequest to make a get call to the API.
// Use ths only if you need to make a call without a timeout.
// For the most part, fetch() will work better, and provide better error handling, among other things.
export const performGetFetchXMLHttp = (endpoint, success, failure, override, printMessage) => {
  if (!endpoint) {
    return;
  }
  endpoint = removeLeadingSlash(endpoint);
  
  // By default we don't want to restart a fetch if one is already running for this endpoint.
  if (!override && abortControllers[endpoint]) {
    if (printMessage) {
      console.warn('Request already in progress: ', endpoint);
    }
    return false;
  }
  
  let fullEndpoint = buildApiUrl(endpoint);
  let xhr = new XMLHttpRequest();

  xhr.onreadystatechange = function () {
    if (this.readyState === 4) {
      if (this.status === 200) {
        success(JSON.parse(this.responseText));
      } else {
        failure(this);
      }
    }
  };

  createAbortController(endpoint, printMessage, xhr)

  xhr.open('GET', fullEndpoint);

  xhr.send();

  return xhr;
}

// Use fetch to make a post call to the API.
export const performPostFetch = (endpoint, requestOptions = {}, success, failure, override,
                                 printMessage, fullEndpoint) => {
  if (!endpoint) {
    return;
  }
  endpoint = removeLeadingSlash(endpoint);
  
  // By default we don't want to restart a fetch if one is already running for this endpoint.
  if (!override && abortControllers[endpoint]) {
    if (printMessage) {
      console.warn('Request already in progress: ', endpoint);
    }
    return false;
  }
  requestOptions.signal = createAbortController(endpoint, printMessage).signal;
  fullEndpoint = fullEndpoint || buildApiUrl(endpoint);
  
  return fetch(fullEndpoint, requestOptions)
    .then((res) => {
      delete abortControllers[endpoint];
      let statusText = res.statusText || '';
      if (!res.ok || (res.status !== 200 && res.status !== 201)) {
        return res.json().then(error_response => {
          let msg = '';
          let drop = ' Missing required parameter in the JSON body or the post body or the query string';
          if (error_response?.errors) {
            for (const err in error_response.errors) {
              if (msg.length > 0) {
                msg = msg.concat('\n');
              }
              msg = msg.concat(error_response.errors[err].replace(drop, ''));
            }
          }
          return new Error(msg);
        })
      }
      return res.json();
    })
    .then(
      (result) => {
        return success(result);
      },
      (error) => {
        return failure(error);
      }
    )
    .catch((error) => {
      return failure(error);
    });
}

// Use fetch to make a post call to the API.
// This is an alternate version of the fetch post above that inserts the request options into the post body more verbosely.
// Sometimes this one works where the above post function doesn't, and vice versa.
// It is currently unknown why this is, but it would be nice to be able to get rid of one of these functions, likely pending API changes.
export const performPostFetchDeclaredBody = (endpoint, requestOptions = {}, success, failure, override, printMessage) => {
  if (!endpoint) {
    return;
  }
  endpoint = removeLeadingSlash(endpoint);
  
  // By default we don't want to restart a fetch if one is already running for this endpoint.
  if (!override && abortControllers[endpoint]) {
    if (printMessage) {
      console.warn('Request already in progress: ', endpoint);
    }
    return false;
  }

  requestOptions.signal = createAbortController(endpoint, printMessage).signal;
  let fullEndpoint = buildApiUrl(endpoint);
  let contentType = requestOptions instanceof FormData ? 'multipart/form-data' : 'application/json';
  let body = requestOptions instanceof FormData ? requestOptions : JSON.stringify(requestOptions);

  return fetch(fullEndpoint, {
      method: 'POST',
      headers: {'Content-Type': contentType},
      body: body
    })
    .then((res) => {
      delete abortControllers[endpoint];
      let statusText = res.statusText || '';
      if (!res.ok) {
        return new Error('Network response was not ok: ' + statusText, res);
      }
      if (res.status !== 200) {
        return new Error('Network response status not 200: ' + statusText, res);
      }
      return res.json();
    })
    .then(
      (result) => {
        return success(result);
      },
      (error) => {
        return failure(error);
      }
    );
}

export const performFormDataPost = (endpoint, formData, success, failure, progress) => {
  if (!endpoint) {
    return;
  }
  endpoint = removeLeadingSlash(endpoint);
  
  let fullEndpoint = buildApiUrl(endpoint);
  let body = formData;
  let oReq = new XMLHttpRequest();

  oReq.open('POST', fullEndpoint, true);
  oReq.onprogress = function(oEvent){
    if (progress !== undefined) {
      progress(oReq.response);
    }
  }
  oReq.onload = function(oEvent) {
    if (oReq.status === 200) {
      if (success !== undefined) {
        success(JSON.parse(this.responseText));
      }
    } else {
      console.log('Error ' + oReq.status + ' occurred when trying to upload your form.');
      if (failure !== undefined) {
        failure(this);
      }
    }
  };
  oReq.send(body);
  return oReq;
}

export const buildApiUrl = (endpoint) => {
  let baseUrl;
  if (window._env_.LOCALHOST) {
    baseUrl = window._env_.APP_API;
  } else {
    baseUrl = window._env_.APP_SECURE_API || window._env_.APP_API || '';
  }
  return baseUrl + endpoint;
}

export const buildSecureApiUrl = (endpoint) => {
  let baseUrl;
  if (window._env_.LOCALHOST) {
    baseUrl = window._env_.APP_API;
  } else {
    baseUrl = window._env_.APP_API.replace('/api', '/secure/api') || '';
  }
  return baseUrl + endpoint;
}

export const showUserGuide = (target) => {
  OpenDocumentsPage(null, window._env_.APP_SITE === 'challenge' ? 'helpDocChallenge' : 'helpDoc', target);
}

export const createAbortController = (name, printMessage, xhr) => {
  // Clear existing abort controller with this name/endpoint
  abortFetch(name, printMessage);

  if (xhr) {
    abortControllers[name] = xhr;
  } else {
    abortControllers[name] = new AbortController();
  }

  return abortControllers[name];
}

export const getAbortControllers = () => {
  return abortControllers;
}

export const abortFetch = (name, printMessage) => {
  // If the named abort controller already exists, assume its fetch is still ongoing, and abort it
  if (abortControllers[name] && abortControllers[name].abort) {
    abortControllers[name].abort();
    abortControllers[name] = undefined;

    if (printMessage) {
      console.warn('Previous request aborted: ', name);
    }
    
    return true;
  }

  if (printMessage) {
    console.warn('No abortable request by name: ', name);
  }
  return false;
}

export const GotoNewPage = (endpoint) => {
  if (endpoint) {
    window.open(endpoint, '_blank').focus();
  }
}

export function sleep(ms) {
    return new Promise(resolve => setTimeout(resolve, ms));
}

export async function pause(i) {
    await sleep(i * 1000);
}

export function openWindowWithPost(url, data) {
    console.log('oWwP', url, isChromeBrowser());
    let form = document.createElement('form');
    form.target = '_blank'
    form.method = 'POST';
    form.action = url;
    form.style.display = 'none';
    form.rel = 'noopener,noreferrer';

    for (let key in data) {
        let input = document.createElement('input');
        input.type = 'hidden';
        input.name = key;
        input.value = data[key];
        form.appendChild(input);
    }

    document.body.appendChild(form);
    form.submit();
    if (isChromeBrowser()) {
      pause(1).then(() => {
        document.body.removeChild(form);
      })
    } else {
      document.body.removeChild(form);
    }
}

export const isChromeBrowser = () => {
  let isChromium = window.chrome;
  let winNav = window.navigator;
  let vendorName = winNav.vendor;
  let isOpera = typeof window.opr !== "undefined";
  let isIEedge = winNav.userAgent.indexOf("Edg") > -1;
  let isIOSChrome = winNav.userAgent.match("CriOS");

  if (isIOSChrome) {
     // is Google Chrome on IOS
    return true
  } else if(
    isChromium !== null &&
    typeof isChromium !== "undefined" &&
    vendorName === "Google Inc." &&
    isOpera === false &&
    isIEedge === false
  ) {
     // is Google Chrome
    return true
  } else {
     // not Google Chrome
    return false
  }
}
